<template>
  <VModal v-if="displayModal" ref="modal" @modal:hidden="$emit('order-item-modal:closed')">
    <div class="rounded-lg shadow-lg mx-auto h-full" @click.stop>
      <div class="rounded-lg shadow-xs bg-white p-2 h-full flex flex-col overflow-hidden">
        <template v-if="loading">
          <div class="p-6 text-center">
            <Loader />
          </div>
        </template>
        <div v-else class="flex-1 h-0">
          <div class="flex h-full space-x-2">
            <template v-if="data.parentOrderItemUuid">
              <LeftToolbar @order:show="showOrder" />

              <div
                v-if="leftColumnVisible"
                class="w-screen max-w-3xl h-full shadow-inner flex flex-col divide-y divide-gray-200 bg-gray-200 rounded overflow-hidden"
              >
                <div class="flex-1 h-0">
                  <div class="overflow-y-auto h-full p-2">
                    <OrderItemParentOrderItem
                      :order-item="data"
                      :can-copy="true"
                      :requirements.sync="data.requirements"
                      :links.sync="data.links"
                      :notes.sync="data.notes"
                    />
                  </div>
                </div>
              </div>
            </template>

            <div class="w-screen max-w-lg h-full flex flex-col divide-y divide-gray-200 bg-white">
              <div class="flex-1 h-0">
                <div class="overflow-y-auto h-full">
                  <OrderItem
                    :order-item="data"
                    :can-edit="true"
                    :submit-status="submitStatus"
                    @update:order-item="updateData"
                    @submit="submitOrderItem"
                  />
                </div>
              </div>
            </div>

            <template v-if="orderItemId">
              <div
                v-if="rightColumnVisible"
                class="w-screen max-w-md h-full shadow-inner flex flex-col divide-y divide-gray-200 bg-gray-200 rounded overflow-hidden"
              >
                <div class="flex-1 h-0">
                  <div class="h-full">
                    <OrderItemComments :order-item-id="orderItemId" />
                  </div>
                </div>
              </div>
            </template>

            <RightToolbar @comments:show="showComments" @close="$refs.modal.toggleModal()" />
          </div>
        </div>
      </div>
    </div>
  </VModal>
</template>

<script>
import { cloneDeep } from 'lodash-es';
import LeftToolbar from './orderItemModal/LeftToolbar.vue';
import RightToolbar from './orderItemModal/RightToolbar.vue';
import OrderItem from './orderItemModal/OrderItem.vue';
import OrderItemComments from './orderItemModal/OrderItemComments.vue';
import OrderItemParentOrderItem from './orderItemModal/OrderItemParentOrderItem.vue';

const STATUS_IDLE = 'STATUS_IDLE';
const STATUS_PENDING = 'STATUS_PENDING';
const STATUS_RESOLVED = 'STATUS_RESOLVED';
const STATUS_REJECTED = 'STATUS_REJECTED';

const defaultData = {
  uuid: null,
  name: null,
  requirements: null,
  status: null,
  links: [],
  notes: [],
  media: [],
  orderUuid: null,
  parentOrderItemUuid: null,
  quantity: 1,
  pricePerUnit: 0,
  price: 0,
  orderableType: null,
  orderableUuid: null,
};

export default {
  name: 'OrderItemModal',

  components: {
    LeftToolbar,
    RightToolbar,
    OrderItem,
    OrderItemComments,
    OrderItemParentOrderItem,
  },

  props: {
    orderItemId: {
      type: String,
      required: true,
    },
    defaultData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      data: null,
      submitStatus: STATUS_IDLE,
      displayModal: false,
      loading: false,
      leftColumnVisible: false,
      rightColumnVisible: true,
    };
  },

  computed: {
    orderItem() {
      return this.$store.state.entities.orderItems.byId[this.orderItemId];
    },
  },

  watch: {
    orderItem: 'setDefaultData',
  },

  created() {
    this.setDefaultData(this.defaultData);
  },

  mounted() {
    this.displayModal = true;
    if (this.orderItemId) {
      this.fetchOrderItem();
    }
  },

  methods: {
    async fetchOrderItem() {
      this.loading = true;

      try {
        await this.$store.dispatch({
          type: 'ui/orderItems/read',
          orderItemUuid: this.orderItemId,
          params: {
            include: 'media,links,notes',
          },
        });

        this.loading = false;
      } catch (error) {
        this.$refs.modal.toggleModal();
      }
    },
    setDefaultData(data) {
      const dataWithDefaults = {
        ...cloneDeep(defaultData),
        ...cloneDeep(data),
      };

      const orderItemData = {
        uuid: dataWithDefaults.uuid,
        name: dataWithDefaults.name,
        requirements: dataWithDefaults.requirements,
        status: dataWithDefaults.status,
        orderUuid: dataWithDefaults.orderUuid,
        parentOrderItemUuid: dataWithDefaults.parentOrderItemUuid,
        quantity: dataWithDefaults.quantity,
        pricePerUnit: dataWithDefaults.pricePerUnit,
        price: dataWithDefaults.price,
        orderableType: dataWithDefaults.orderableType,
        orderableUuid: dataWithDefaults.orderableUuid,
        links: dataWithDefaults.links.map((idOrLink) => {
          if (typeof idOrLink === 'string') {
            return this.$store.state.entities.links.byId[idOrLink];
          }
          return idOrLink;
        }),
        notes: dataWithDefaults.notes.map((idOrNote) => {
          if (typeof idOrNote === 'string') {
            return this.$store.state.entities.notes.byId[idOrNote];
          }
          return idOrNote;
        }),
        media: dataWithDefaults.media.map((idOrMedium) => {
          if (typeof idOrMedium === 'string') {
            return this.$store.state.entities.media.byId[idOrMedium];
          }
          return idOrMedium;
        }),
      };

      this.data = {
        ...defaultData,
        ...orderItemData,
      };
    },
    async submitOrderItem() {
      if (this.submitStatus === STATUS_PENDING) {
        return;
      }

      this.submitStatus = STATUS_PENDING;

      try {
        await this.$store.dispatch({
          type: 'ui/orderItems/update',
          id: this.orderItemId,
          body: this.data,
        });

        this.$emit('order-item:updated');

        this.submitStatus = STATUS_RESOLVED;
      } catch (error) {
        // should handle 244 errors
        this.submitStatus = STATUS_REJECTED;
      } finally {
        setTimeout(() => {
          this.submitStatus = STATUS_IDLE;
        }, 1000);
      }
    },
    showOrder() {
      if (this.leftColumnVisible) {
        this.leftColumnVisible = false;
      } else {
        this.rightColumnVisible = false;
        this.leftColumnVisible = true;
      }
    },
    showComments() {
      if (this.rightColumnVisible) {
        this.rightColumnVisible = false;
      } else {
        this.leftColumnVisible = false;
        this.rightColumnVisible = true;
      }
    },
    updateData(newData) {
      this.data = { ...newData };
    },
  },
};
</script>
